// Универсальная функция для выделения ссылок в тексте (http, https, ftp, mailto, tel, www)
// Также распознает и преобразует идентификаторы тикетов в формате PREFIX-NUMBER в ссылки
// Обрезает завершающую пунктуацию, возвращает массив React-элементов
import React from 'react';
import { Link } from 'react-router-dom';

export default function parseLinks(text, projects = []) {
  if (!text) return null;
  // Универсальная регулярка для URI (http, https, ftp, mailto, tel, www)
  const urlRegex = /((?:https?|ftp):\/\/[\w\-._~:\/?#[\]@!$&'()*+,;=]+|(mailto:[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,})|(tel:\+?[\d\-() ]{5,})|(www\.[\w\-._~:\/?#[\]@!$&'()*+,;=]+))/gi;
  
  // Создаем список существующих префиксов проектов
  const existingPrefixes = projects
    .filter(project => project.ticketPrefix && project.ticketPrefix.trim())
    .map(project => project.ticketPrefix.trim());
  
  // Регулярка для идентификаторов тикетов в формате PREFIX-NUMBER
  // Теперь проверяем только существующие префиксы
  const ticketIdRegex = existingPrefixes.length > 0 
    ? new RegExp(`\\b(${existingPrefixes.map(prefix => prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})-([0-9]{1,6})\\b`, 'g')
    : null; // Если нет префиксов, не ищем тикеты
  
  // Объединяем текст и URL-ы с идентификаторами тикетов
  const segments = [];
  let currentIndex = 0;
  
  // Функция для добавления текстового сегмента между найденными совпадениями
  const addTextSegment = (start, end) => {
    if (start < end) {
      segments.push({
        type: 'text',
        content: text.substring(start, end),
        index: start
      });
    }
  };
  
  // Поиск URL-ов
  while (true) {
    const urlMatch = urlRegex.exec(text);
    if (!urlMatch) break;
    
    addTextSegment(currentIndex, urlMatch.index);
    
    let url = urlMatch[0];
    // Обрезаем завершающие символы пунктуации
    let trailing = '';
    while (url && /[.,!?:;\]\)"'}]/.test(url[url.length - 1])) {
      trailing = url[url.length - 1] + trailing;
      url = url.slice(0, -1);
    }
    
    segments.push({
      type: 'url',
      content: url,
      trailing: trailing,
      index: urlMatch.index
    });
    
    currentIndex = urlMatch.index + urlMatch[0].length;
  }
  
  // Добавляем оставшийся текст для поиска идентификаторов тикетов
  addTextSegment(currentIndex, text.length);
  
  // Поиск идентификаторов тикетов в текстовых сегментах
  const finalSegments = [];
  
  for (const segment of segments) {
    if (segment.type === 'url') {
      finalSegments.push(segment);
      continue;
    }
    
    const textContent = segment.content;
    let lastTicketIndex = 0;
    let ticketMatch;
    const textSegments = [];
    
    // Если нет регулярки для тикетов, просто добавляем текст как есть
    if (!ticketIdRegex) {
      textSegments.push({
        type: 'text',
        content: textContent,
        index: segment.index
      });
    } else {
      // Сбрасываем lastIndex регулярного выражения
      ticketIdRegex.lastIndex = 0;
      
      while ((ticketMatch = ticketIdRegex.exec(textContent)) !== null) {
        if (ticketMatch.index > lastTicketIndex) {
          textSegments.push({
            type: 'text',
            content: textContent.substring(lastTicketIndex, ticketMatch.index),
            index: segment.index + lastTicketIndex
          });
        }
        
        // Проверяем, что найденный префикс действительно существует в системе
        const foundPrefix = ticketMatch[1];
        const isValidPrefix = existingPrefixes.length === 0 || existingPrefixes.includes(foundPrefix);
        
        if (isValidPrefix) {
          textSegments.push({
            type: 'ticket',
            content: ticketMatch[0],
            prefix: ticketMatch[1],
            id: ticketMatch[2],
            index: segment.index + ticketMatch.index
          });
        } else {
          // Если префикс не найден в системе, добавляем как обычный текст
          textSegments.push({
            type: 'text',
            content: ticketMatch[0],
            index: segment.index + ticketMatch.index
          });
        }
        
        lastTicketIndex = ticketMatch.index + ticketMatch[0].length;
      }
      
      if (lastTicketIndex < textContent.length) {
        textSegments.push({
          type: 'text',
          content: textContent.substring(lastTicketIndex),
          index: segment.index + lastTicketIndex
        });
      }
    }
    
    finalSegments.push(...textSegments);
  }
  
  // Сортируем сегменты по индексу
  finalSegments.sort((a, b) => a.index - b.index);
  
  // Преобразуем сегменты в React элементы
  const parts = [];
  let lastProcessedIndex = 0;
  
  for (const segment of finalSegments) {
    // Обрабатываем каждый сегмент в зависимости от его типа
    switch (segment.type) {
      case 'text':
        // Разбиваем текст по переносам строк
        const lines = segment.content.split('\n');
        for (let i = 0; i < lines.length; i++) {
          if (i > 0) {
            // Добавляем <br /> для каждого переноса строки
            parts.push(<br key={`br-${segment.index}-${i}`} />);
          }
          if (lines[i]) {
            parts.push(lines[i]);
          }
        }
        break;
        
      case 'url':
        let href = segment.content;
        if (/^www\./i.test(segment.content)) {
          href = 'http://' + segment.content;
        }
        
        parts.push(
          <a key={`url-${segment.index}`} href={href} target={/^mailto:|^tel:/i.test(href) ? undefined : '_blank'} rel={!/^mailto:|^tel:/i.test(href) ? 'noopener noreferrer' : undefined} style={{ color: '#1976d2', textDecoration: 'underline', wordBreak: 'break-all' }}>
            {segment.content}
          </a>
        );
        
        if (segment.trailing) parts.push(segment.trailing);
        break;
        
      case 'ticket':
        // Создаем обычную HTML ссылку вместо компонента Link для открытия в новом окне
        parts.push(
          <a key={`ticket-${segment.index}`} href={`/#/ticket/${segment.content}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 'bold' }}>
            {segment.content}
          </a>
        );
        break;
    }
  }
  
  return parts.length === 0 ? null : parts;
}
